import { Fragment, useState, type ReactNode } from 'react'
import {
  AlertCircle,
  CalendarCheck,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  RefreshCw,
  Search,
  ShieldCheck,
  UserX,
  Zap,
} from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import { LoadingTable } from '../ui/loading-states'
import { useScheduleEffectivenessReport } from '../../hooks/useReports'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { exportScheduleEffectivenessToExcel } from '../../utils/exportScheduleEffectivenessToExcel'
import type {
  ScheduleEffectivenessFilters,
  ScheduleEffectivenessRow,
} from '../../types/reports.types'

interface Organization {
  id: string
  name: string
}

interface Department {
  id: string
  name: string
}

function currentMonthRange(): { from: string; to: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const last = new Date(y, m + 1, 0).getDate()
  const mm = String(m + 1).padStart(2, '0')
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` }
}

function fmtPct(value: number | null): string {
  return value === null ? '—' : `${value}%`
}

function changePctClass(value: number | null): string {
  if (value === null) return 'text-muted-foreground'
  if (value > 30) return 'text-red-600 font-semibold'
  if (value > 15) return 'text-amber-600 font-semibold'
  return 'text-green-700'
}

function adherencePctClass(value: number | null): string {
  if (value === null) return 'text-muted-foreground'
  if (value < 80) return 'text-red-600 font-semibold'
  if (value < 90) return 'text-amber-600 font-semibold'
  return 'text-green-700'
}

// Пояснения колонок; для расчётных — формула из других колонок
const COLUMN_HINTS = {
  employees: 'Активные сотрудники этой должности в подразделении.',
  basePlan:
    'План (база): смены за период по графику, привязанному в админке ' +
    '(шаблон или персональный график), БЕЗ учёта исключений. ' +
    'То, что запланировал HR.',
  exceptions:
    'Сколько исключений создал управляющий за период. Замена считается ' +
    'одним исключением (автоматическая запись сменщика не учитывается).',
  changePct:
    'Формула: Исключ. ÷ План (база) × 100. Какая доля базового плана ' +
    'перекроена исключениями.',
  added: '+Смена: по базовому графику был выходной — сделали рабочим днём.',
  removed: '−Смена: по базовому графику был рабочий день — сделали выходным.',
  timeChanged:
    'Время: день рабочий и по базе, и по исключению — изменено только ' +
    'время смены.',
  replacements: 'Замена: смена передана другому сотруднику.',
  actual:
    'Факт: сколько смен реально отработано за прошедшие дни периода — ' +
    'по отметкам прихода. Сегодняшний день не учитывается. Сюда входят ' +
    'и выходы без графика.',
  noShows:
    'Неявки («холостые смены»): смена стояла в итоговом графике ' +
    '(с учётом исключений) в прошедшем дне, а отметки о выходе нет.',
  adherencePct:
    'Формула: (плановые смены прошедших дней − Неявки) ÷ плановые смены ' +
    'прошедших дней × 100. Не «Факт ÷ План», потому что в Факт входят ' +
    'и выходы без графика.',
} as const

const AUTHOR_HINTS = {
  user: 'Кто создал исключения (пользователь админки). ФИО привязанного сотрудника или email.',
  exceptions:
    'Сколько исключений этот пользователь создал за период (замена = 1).',
  departments: 'Подразделения, в которых пользователь менял график.',
} as const

const CARD_HINTS = {
  stability:
    'Формула: (План (база) − Исключения) ÷ План (база) × 100. ' +
    'Какая доля запланированных смен осталась нетронутой.',
  exceptions:
    'Все изменения графика исключениями за период по выбранным фильтрам.',
  adherence:
    'Формула: (плановые смены прошедших дней − Холостые смены) ÷ плановые ' +
    'смены прошедших дней × 100.',
  noShows:
    'Смена стояла в итоговом графике в прошедшем дне, а отметки о выходе ' +
    'нет. Сегодняшний день не учитывается.',
} as const

function HintTooltip({ text, children }: { text: string; children: ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          <p className="text-xs">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function HintHead({
  hint,
  children,
  className = 'text-center',
}: {
  hint: string
  children: ReactNode
  className?: string
}) {
  return (
    <TableHead className={className}>
      <HintTooltip text={hint}>
        <span className="cursor-help">{children}</span>
      </HintTooltip>
    </TableHead>
  )
}

function RowCells({ row }: { row: ScheduleEffectivenessRow }) {
  return (
    <>
      <TableCell className="text-center">{row.employees}</TableCell>
      <TableCell className="text-center font-medium">{row.base_plan}</TableCell>
      <TableCell className="text-center font-medium">{row.exceptions_total}</TableCell>
      <TableCell className={`text-center ${changePctClass(row.change_pct)}`}>
        {fmtPct(row.change_pct)}
      </TableCell>
      <TableCell className="text-center text-green-700">{row.added}</TableCell>
      <TableCell className="text-center text-red-600">{row.removed}</TableCell>
      <TableCell className="text-center text-blue-700">{row.time_changed}</TableCell>
      <TableCell className="text-center text-amber-700">{row.replacements}</TableCell>
      <TableCell className="text-center font-medium">{row.actual}</TableCell>
      <TableCell className="text-center text-red-600">{row.no_shows}</TableCell>
      <TableCell className={`text-center ${adherencePctClass(row.adherence_pct)}`}>
        {fmtPct(row.adherence_pct)}
      </TableCell>
    </>
  )
}

export function ScheduleEffectivenessReport() {
  const [filters, setFilters] = useState<ScheduleEffectivenessFilters>(currentMonthRange())
  const [shouldFetch, setShouldFetch] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data: organizations } = useOrganizationsForSelect() as {
    data: Organization[] | undefined
  }
  const { data: departments } = useDepartmentsForSelect(filters.organizationId || null) as {
    data: Department[] | undefined
  }

  const { data: report, isLoading, isFetching, error, refetch } =
    useScheduleEffectivenessReport(filters, shouldFetch)

  const handleGenerate = () => {
    setShouldFetch(true)
    if (shouldFetch) refetch()
  }

  const toggleRow = (departmentId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(departmentId)) next.delete(departmentId)
      else next.add(departmentId)
      return next
    })
  }

  const handleExport = async () => {
    if (report && report.departments.length > 0) {
      try {
        await exportScheduleEffectivenessToExcel(report)
      } catch (e) {
        console.error('Schedule effectiveness export failed:', e)
      }
    }
  }

  const canExport = !!report && report.departments.length > 0

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="se-from">С</Label>
              <input
                id="se-from"
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                className="h-10 w-[160px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="se-to">По</Label>
              <input
                id="se-to"
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                className="h-10 w-[160px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="se-org">Организация</Label>
              <select
                id="se-org"
                value={filters.organizationId || ''}
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    organizationId: e.target.value || undefined,
                    departmentId: undefined,
                  }))
                }
                className="h-10 w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Все организации</option>
                {organizations?.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="se-dept">Подразделение</Label>
              <select
                id="se-dept"
                value={filters.departmentId || ''}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, departmentId: e.target.value || undefined }))
                }
                className="h-10 w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Все подразделения</option>
                {departments?.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <Button onClick={handleGenerate} disabled={isFetching || !filters.from || !filters.to}>
              {isFetching ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Сформировать
            </Button>

            <Button variant="outline" onClick={handleExport} disabled={!canExport}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            Не удалось сформировать отчёт: {error instanceof Error ? error.message : 'ошибка'}
          </CardContent>
        </Card>
      )}

      {isLoading && shouldFetch && <LoadingTable rows={8} />}

      {report && (
        <>
          {/* Summary cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <HintTooltip text={CARD_HINTS.stability}>
                  <CardTitle className="text-sm font-medium cursor-help">
                    Стабильность графика
                  </CardTitle>
                </HintTooltip>
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${adherencePctClass(report.summary.stability_pct)}`}>
                  {fmtPct(report.summary.stability_pct)}
                </div>
                <p className="text-xs text-muted-foreground">
                  план (база): {report.summary.base_plan} смен
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <HintTooltip text={CARD_HINTS.exceptions}>
                  <CardTitle className="text-sm font-medium cursor-help">
                    Исключений за период
                  </CardTitle>
                </HintTooltip>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{report.summary.exceptions_total}</div>
                <p className="text-xs text-muted-foreground">
                  изменений графика управляющими
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <HintTooltip text={CARD_HINTS.adherence}>
                  <CardTitle className="text-sm font-medium cursor-help">
                    Соблюдение графика
                  </CardTitle>
                </HintTooltip>
                <CalendarCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${adherencePctClass(report.summary.adherence_pct)}`}>
                  {fmtPct(report.summary.adherence_pct)}
                </div>
                <p className="text-xs text-muted-foreground">
                  вышли на {report.summary.past_planned - report.summary.no_shows} из{' '}
                  {report.summary.past_planned} плановых смен
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <HintTooltip text={CARD_HINTS.noShows}>
                  <CardTitle className="text-sm font-medium cursor-help">
                    Холостые смены
                  </CardTitle>
                </HintTooltip>
                <UserX className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{report.summary.no_shows}</div>
                <p className="text-xs text-muted-foreground">
                  запланирован, но не вышел (прошедшие дни)
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Departments table */}
          <Card>
            <CardHeader>
              <CardTitle>По подразделениям и должностям</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[220px]">Подразделение / Должность</TableHead>
                      <HintHead hint={COLUMN_HINTS.employees}>Сотр.</HintHead>
                      <HintHead hint={COLUMN_HINTS.basePlan}>План (база)</HintHead>
                      <HintHead hint={COLUMN_HINTS.exceptions}>Исключ.</HintHead>
                      <HintHead hint={COLUMN_HINTS.changePct}>% изм.</HintHead>
                      <HintHead hint={COLUMN_HINTS.added}>+Смена</HintHead>
                      <HintHead hint={COLUMN_HINTS.removed}>−Смена</HintHead>
                      <HintHead hint={COLUMN_HINTS.timeChanged}>Время</HintHead>
                      <HintHead hint={COLUMN_HINTS.replacements}>Замена</HintHead>
                      <HintHead hint={COLUMN_HINTS.actual}>Факт</HintHead>
                      <HintHead hint={COLUMN_HINTS.noShows}>Неявки</HintHead>
                      <HintHead hint={COLUMN_HINTS.adherencePct}>% соблюд.</HintHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.departments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center text-muted-foreground py-8">
                          Нет данных за выбранный период
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.departments.map((dept) => {
                        const isOpen = expanded.has(dept.department_id)
                        return (
                          <Fragment key={dept.department_id}>
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50 bg-muted/30"
                              onClick={() => toggleRow(dept.department_id)}
                            >
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-1">
                                  {isOpen ? (
                                    <ChevronDown className="h-4 w-4 shrink-0" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 shrink-0" />
                                  )}
                                  <div>
                                    <div>{dept.department_name}</div>
                                    {dept.organization_name && (
                                      <div className="text-xs text-muted-foreground font-normal">
                                        {dept.organization_name}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <RowCells row={dept} />
                            </TableRow>
                            {isOpen &&
                              dept.positions.map((pos) => (
                                <TableRow key={`${dept.department_id}-${pos.position_id}`}>
                                  <TableCell className="pl-10 text-sm text-muted-foreground">
                                    {pos.position_name}
                                  </TableCell>
                                  <RowCells row={pos} />
                                </TableRow>
                              ))}
                          </Fragment>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Authors */}
          <Card>
            <CardHeader>
              <CardTitle>Кто меняет график</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <HintHead hint={AUTHOR_HINTS.user} className="min-w-[200px]">
                        Пользователь
                      </HintHead>
                      <HintHead hint={AUTHOR_HINTS.exceptions}>Исключений</HintHead>
                      <HintHead hint={COLUMN_HINTS.added}>+Смена</HintHead>
                      <HintHead hint={COLUMN_HINTS.removed}>−Смена</HintHead>
                      <HintHead hint={COLUMN_HINTS.timeChanged}>Время</HintHead>
                      <HintHead hint={COLUMN_HINTS.replacements}>Замена</HintHead>
                      <HintHead hint={AUTHOR_HINTS.departments} className="">
                        Подразделения
                      </HintHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.authors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Исключений за период нет
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.authors.map((author, idx) => (
                        <TableRow key={author.user_id ?? `unknown-${idx}`}>
                          <TableCell className="font-medium">{author.user_name}</TableCell>
                          <TableCell className="text-center font-medium">
                            {author.exceptions_total}
                          </TableCell>
                          <TableCell className="text-center text-green-700">{author.added}</TableCell>
                          <TableCell className="text-center text-red-600">{author.removed}</TableCell>
                          <TableCell className="text-center text-blue-700">{author.time_changed}</TableCell>
                          <TableCell className="text-center text-amber-700">{author.replacements}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[400px]">
                            {author.departments.join(', ')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
